import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Eye, Download, Copy, Share2, Palette, Trash2, Camera } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import type { GenerationResult } from '@/utils/visualGenerationHandlers';

interface GenerationResultCardProps {
  result: GenerationResult;
  onPreview?: (imageUrl: string) => void;
  onDownload?: (imageUrl: string, filename: string) => void;
  onUseAsInput?: (imageUrl: string) => void;
  onSetAsPreview?: (result: GenerationResult) => void;
  onDelete?: (result: GenerationResult) => void;
  className?: string;
}

export const GenerationResultCard: React.FC<GenerationResultCardProps> = ({
  result,
  onPreview,
  onDownload,
  onUseAsInput,
  onSetAsPreview,
  onDelete,
  className
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-500/10 text-green-700 border-green-300';
      case 'error': return 'bg-red-500/10 text-red-700 border-red-300';
      case 'generating': return 'bg-blue-500/10 text-blue-700 border-blue-300';
      default: return 'bg-gray-500/10 text-gray-700 border-gray-300';
    }
  };

  const getPlatformIcon = (platform: string) => {
    // You can add platform-specific icons here
    return <Palette className="h-3 w-3" />;
  };

  const purposeInfo = result.purposeMetadata;

  return (
    <div className={cn("relative group", className)}>
      {/* Image/Video Preview */}
      {result.status === 'completed' && (result.imageUrl || result.imageUrls || result.videoUrl) ? (
        <div className="relative">
          {result.videoUrl ? (
            <video
              src={result.videoUrl}
              className="w-full h-56 object-cover rounded-lg"
              controls
              muted
              loop
            />
          ) : result.imageUrls && result.imageUrls.length > 1 ? (
            // Multi-image grid layout
            <div className="w-full h-56 rounded-lg overflow-hidden">
              <div className={cn(
                "grid gap-1 h-full",
                result.imageUrls.length === 2 ? "grid-cols-2" :
                result.imageUrls.length === 3 ? "grid-cols-3" :
                result.imageUrls.length === 4 ? "grid-cols-2 grid-rows-2" :
                "grid-cols-3 grid-rows-2"
              )}>
                {result.imageUrls.slice(0, 6).map((imageUrl, index) => (
                  <img
                    key={index}
                    src={imageUrl}
                    alt={`Generated result ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                ))}
                {result.imageUrls.length > 6 && (
                  <div className="bg-black/70 flex items-center justify-center text-white text-sm font-medium">
                    +{result.imageUrls.length - 6}
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Single image
            <img
              src={result.imageUrl || result.imageUrls?.[0]}
              alt="Generated result"
              className="w-full h-56 object-cover rounded-lg"
            />
          )}
          
          {/* Generation type badge for multi-image */}
          {result.imageUrls && result.imageUrls.length > 1 && (
            <div className="absolute top-2 left-2 z-10">
              <Badge variant="secondary" className="text-xs bg-white/90 text-gray-800">
                {result.generationType === 'batch' ? `${result.imageUrls.length} variants` : 'Single'}
              </Badge>
            </div>
          )}
          
          {/* Centered action icons overlay */}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-3">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => onPreview?.((result.imageUrl || result.imageUrls?.[0] || result.videoUrl)!)}
                    className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                  >
                    <Eye className="h-5 w-5 text-white" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Preview {result.videoUrl ? 'video' : result.imageUrls && result.imageUrls.length > 1 ? 'images' : 'image'}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => {
                      try {
                        const isVideo = !!result.videoUrl;
                        const hasMultipleImages = result.imageUrls && result.imageUrls.length > 1;
                        
                        if (hasMultipleImages) {
                          // Download all images as a zip or individually
                          result.imageUrls!.forEach((imageUrl, index) => {
                            const link = document.createElement('a');
                            link.href = imageUrl;
                            link.download = `${purposeInfo?.platform || 'generated'}-${purposeInfo?.format || 'visual'}-${index + 1}-${Date.now()}.png`;
                            link.target = '_blank';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          });
                          onDownload?.(result.imageUrls[0], `${result.imageUrls.length}-images`);
                        } else {
                          const fileExt = isVideo ? 'mp4' : 'png';
                          const filename = `${purposeInfo?.platform || 'generated'}-${purposeInfo?.format || 'visual'}-${Date.now()}.${fileExt}`;
                          
                          const link = document.createElement('a');
                          link.href = (result.imageUrl || result.imageUrls?.[0] || result.videoUrl)!;
                          link.download = filename;
                          link.target = '_blank';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          
                          onDownload?.((result.imageUrl || result.imageUrls?.[0] || result.videoUrl)!, filename);
                        }
                        
                        // Show success toast
                        const message = result.imageUrls && result.imageUrls.length > 1 
                          ? `Download started for ${result.imageUrls.length} images`
                          : 'Download started';
                        toast.success(message);
                      } catch (error) {
                        console.error('Download failed:', error);
                        toast.error(`Download failed. Please try right-clicking and saving the ${result.videoUrl ? 'video' : result.imageUrls && result.imageUrls.length > 1 ? 'images' : 'image'}.`);
                      }
                    }}
                    className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                  >
                    <Download className="h-5 w-5 text-white" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Download {result.videoUrl ? 'video' : result.imageUrls && result.imageUrls.length > 1 ? `${result.imageUrls.length} images` : 'image'}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => onUseAsInput?.((result.imageUrl || result.imageUrls?.[0] || result.videoUrl)!)}
                    className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                  >
                    <Copy className="h-5 w-5 text-white" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Use as input</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {/* Set as Preview button - only show for images with prompt_id */}
            {(result.imageUrl || result.imageUrls?.[0]) && result.prompt && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => onSetAsPreview?.(result)}
                      className="p-2 bg-blue-500/80 hover:bg-blue-500 rounded-full transition-colors"
                    >
                      <Camera className="h-5 w-5 text-white" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Set as preview</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => onDelete?.(result)}
                    className="p-2 bg-red-500/80 hover:bg-red-500 rounded-full transition-colors"
                  >
                    <Trash2 className="h-5 w-5 text-white" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Delete</p>
                </TooltipContent>
                </Tooltip>
              </TooltipProvider>
          </div>
        </div>
      ) : result.status === 'generating' ? (
        <div className="relative">
          <div className="w-full h-56 bg-muted rounded-lg flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
          
          {/* Delete option for generating items */}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => onDelete?.(result)}
                    className="p-2 bg-red-500/80 hover:bg-red-500 rounded-full transition-colors"
                  >
                    <Trash2 className="h-5 w-5 text-white" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Delete</p>
                </TooltipContent>
                </Tooltip>
              </TooltipProvider>
          </div>
        </div>
      ) : result.status === 'error' ? (
        <div className="relative">
          <div className="w-full h-56 bg-red-50 rounded-lg flex items-center justify-center text-red-600">
            <div className="text-center">
              <div className="text-sm font-medium">Generation Failed</div>
              <div className="text-xs mt-1">{result.error}</div>
            </div>
          </div>
          
          {/* Delete option for error items */}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => onDelete?.(result)}
                    className="p-2 bg-red-500/80 hover:bg-red-500 rounded-full transition-colors"
                  >
                    <Trash2 className="h-5 w-5 text-white" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Delete</p>
                </TooltipContent>
                </Tooltip>
              </TooltipProvider>
          </div>
        </div>
      ) : null}
    </div>
  );
};